import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  XCircle,
  Minus,
  ArrowRight,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/buildxact-alternative";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Buildxact Alternative UK",
  "description": "Looking for a Buildxact alternative in the UK? The Proper Tool offers AI quote generation, a 31-category UK cost library, and a 14-day free trial.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Compare", "item": "https://thepropertool.com/comparisons" },
    { "@type": "ListItem", "position": 3, "name": "Buildxact Alternative", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "What is the best Buildxact alternative for UK builders?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "For UK residential builders, The Proper Tool is one alternative to Buildxact. Buildxact has UK plans and advertises monthly Foundation £119, Pro £209 and Master £349 list pricing, with annual-equivalent prices of £89, £160 and £262. It offers digital takeoffs, supplier catalogues and live pricing; UK suitability should be assessed against your workflow.",
      },
    },
    {
      "@type": "Question",
      "name": "Does Buildxact work in the UK?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. Buildxact has UK plans and a UK site. Its supplier catalogues and live pricing are different from a universal UK construction cost book, so check the available suppliers and rates for your work.",
      },
    },
    {
      "@type": "Question",
      "name": "How does Buildxact pricing compare to The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Buildxact UK list pricing is £119 Foundation, £209 Pro and £349 Master monthly, or annual-equivalent £89, £160 and £262. Feature pages advertise a 14-day trial. The Proper Tool starts at £29 per month with a 14-day free trial.",
      },
    },
    {
      "@type": "Question",
      "name": "Can Buildxact read structural drawings and PDFs?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Buildxact has digital takeoff functionality that allows builders to import clean architectural drawings and manually mark up dimensions. It does not automatically interpret PDFs, extract specifications from drawings, or generate scopes from plain-language descriptions. The Proper Tool reads uploaded structural drawing PDFs and uses AI to extract beam sizes, floor areas, and dimensions to generate a more complete quote.",
      },
    },
    {
      "@type": "Question",
      "name": "How long does it take to get started with Buildxact vs The Proper Tool?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Buildxact requires significant setup: configuring materials libraries, rate schedules, and estimate templates before the first quote can be produced. This typically takes hours to days. The Proper Tool can produce a first quote in under 10 minutes with no upfront configuration. Its UK cost library is built in, and the AI generates the scope from a description or uploaded drawings.",
      },
    },
  ],
};

type TriState = "yes" | "partial" | "no";

interface ComparisonRow {
  feature: string;
  buildxact: string;
  buildxactState: TriState;
  tpt: string;
  tptState: TriState;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  { feature: "UK-specific construction pricing", buildxact: "Supplier catalogues/live pricing", buildxactState: "partial", tpt: "Yes: 31 UK trade categories", tptState: "yes" },
  { feature: "AI scope generation from description", buildxact: "No", buildxactState: "no", tpt: "Yes", tptState: "yes" },
  { feature: "Reads structural drawings and PDFs", buildxact: "Takeoff only (manual)", buildxactState: "partial", tpt: "Yes: full AI interpretation", tptState: "yes" },
  { feature: "Expert scope completeness validation", buildxact: "No", buildxactState: "no", tpt: "Yes: built-in review", tptState: "yes" },
  { feature: "Labour / materials / plant split", buildxact: "Partial", buildxactState: "partial", tpt: "Yes: per line item", tptState: "yes" },
  { feature: "Suited to extensions and alterations", buildxact: "Partial: new-build focus", buildxactState: "partial", tpt: "Yes: built for this", tptState: "yes" },
  { feature: "Job cost management", buildxact: "Yes", buildxactState: "yes", tpt: "No (quote-focused)", tptState: "no" },
  { feature: "Project scheduling", buildxact: "Yes", buildxactState: "yes", tpt: "No (quote-focused)", tptState: "no" },
  { feature: "Branded PDF quote output", buildxact: "Yes", buildxactState: "yes", tpt: "Yes", tptState: "yes" },
  { feature: "Free trial length", buildxact: "14 days", buildxactState: "partial", tpt: "14 days", tptState: "yes" },
  { feature: "Starting price", buildxact: "£119/mo Foundation", buildxactState: "partial", tpt: "£29/mo (per account)", tptState: "yes" },
  { feature: "Credit card required to trial", buildxact: "Yes", buildxactState: "no", tpt: "No", tptState: "yes" },
];

function Cell({ value, state }: { value: string; state: TriState }) {
  if (state === "yes") return (
    <span className="flex items-start gap-1.5 text-green-600 dark:text-green-400 font-medium">
      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  if (state === "no") return (
    <span className="flex items-start gap-1.5 text-red-500 dark:text-red-400">
      <XCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
  return (
    <span className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
      <Minus className="h-4 w-4 flex-shrink-0 mt-0.5" />{value}
    </span>
  );
}

const WHY_LOOK = [
  {
    title: "Supplier pricing is not a universal UK cost book",
    desc: "Buildxact offers supplier catalogues and live pricing. That is useful for connected purchasing workflows, but it is not the same as an included universal UK construction cost library.",
  },
  {
    title: "No AI scope generation from descriptions",
    desc: "Buildxact requires manual takeoff from drawings. There's no way to describe a job in plain language and have the software generate a complete scope. For builders doing extensions and loft conversions from architects' briefs, this adds significant manual work.",
  },
  {
    title: "Significant setup time before the first quote",
    desc: "Buildxact's estimating templates and materials libraries need to be configured before they produce useful output. This often takes hours or days, whereas tools built around pre-loaded UK data can generate a first quote in minutes.",
  },
  {
    title: "Price point designed for larger operations",
    desc: "At £119 Foundation, £209 Pro and £349 Master monthly list pricing, Buildxact should be assessed against the value of Digital Takeoffs, supplier catalogues, live pricing and project-management features for your operation.",
  },
];

const STAY_ON_BUILDXACT = [
  "You do high-volume new-build residential work and need detailed digital takeoff from clean architectural drawings.",
  "You have already invested the time to configure Buildxact for UK rates and have built working templates.",
  "You need the full job cost management and scheduling features alongside estimating, not just a quoting tool.",
  "You need supplier catalogues, live pricing and digital takeoffs alongside estimating.",
];

const SWITCH_REASONS = [
  "You need a universal built-in UK construction cost book rather than supplier catalogues and live pricing.",
  "You want to generate a complete quote from a description or drawing without spending hours on manual takeoff.",
  "Your work is extensions, loft conversions, and structural alterations rather than ground-up new build.",
  "You want to be quoting professionally within minutes, not after days of template configuration.",
  "Your budget suits a tool priced for UK small builders rather than enterprise-scale estimating software.",
  "You want a 14-day free trial with no credit card required and clear pricing before you commit.",
];

const FAQS = [
  {
    q: "What is the best Buildxact alternative for UK builders?",
    a: "For UK residential builders, The Proper Tool is one Buildxact alternative. Buildxact has UK plans, Digital Takeoffs, supplier catalogues and live pricing. Its published UK list pricing is £119 Foundation, £209 Pro and £349 Master monthly, with annual-equivalent prices of £89, £160 and £262.",
  },
  {
    q: "Does Buildxact work in the UK?",
    a: "Buildxact has UK plans and a UK site. It offers supplier catalogues and live pricing rather than a universal built-in UK construction cost book. Digital Takeoffs and Blu features depend on the selected plan or add-on.",
  },
  {
    q: "How does Buildxact pricing compare to The Proper Tool?",
    a: "Buildxact UK list pricing is £119 Foundation, £209 Pro and £349 Master monthly, or annual-equivalent £89, £160 and £262. Feature pages advertise a 14-day trial. The Proper Tool starts at £29 per month with a 14-day free trial.",
  },
  {
    q: "Can Buildxact read structural drawings and PDFs?",
    a: "Buildxact has digital takeoff functionality that allows builders to import clean architectural drawings and manually mark up dimensions. It does not automatically interpret PDFs or generate scopes from plain-language descriptions. The Proper Tool reads uploaded structural drawing PDFs and uses AI to extract beam sizes, floor areas, and dimensions automatically.",
  },
  {
    q: "How long does it take to get started with Buildxact vs The Proper Tool?",
    a: "Buildxact requires significant setup: configuring materials libraries, rate schedules, and estimate templates before the first quote can be produced. This typically takes hours to days. The Proper Tool can produce a first quote in under 10 minutes with no upfront configuration, as its UK cost library is built in.",
  },
];

export default function BuildxactAlternativePage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Buildxact Alternative UK: Construction Estimating Software | The Proper Tool"
        description="Looking for a Buildxact alternative built for the UK market? The Proper Tool offers AI quote generation, a 31-category UK cost library, structural drawing analysis, and a 14-day free trial from £29/mo."
        canonical="/buildxact-alternative"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Buildxact Alternative</span>
            </div>
            <span className="inline-block text-xs font-semibold px-3 py-1 rounded-full bg-primary/10 text-primary mb-4">Comparison</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Buildxact Alternative UK: which tool suits small builders better?
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Buildxact has UK plans, Digital Takeoffs and supplier pricing. If you are a UK builder doing extensions, loft conversions or residential alterations, here is how The Proper Tool compares.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/login">
                  Start free, no credit card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="#comparison-table">See the comparison</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · From £29/mo · No credit card required</p>
          </div>
        </section>

        {/* Why builders look */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Why UK builders look for a Buildxact alternative</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Buildxact is a capable estimating and job management platform with UK plans. UK residential builders should assess the following distinctions:
            </p>
            <div className="space-y-4">
              {WHY_LOOK.map((item) => (
                <div key={item.title} className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-1" />
                  <div>
                    <span className="font-semibold">{item.title}: </span>
                    <span className="text-muted-foreground">{item.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Feature comparison table */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40" id="comparison-table">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">The Proper Tool vs Buildxact: feature comparison</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                A factual side-by-side comparison for UK residential builders.
              </p>
            </div>
            <div className="overflow-x-auto rounded-xl border border-border">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th className="text-left px-5 py-4 font-semibold text-sm w-2/5">Feature</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%]">Buildxact</th>
                    <th className="text-left px-5 py-4 font-semibold text-sm w-[30%] text-primary">The Proper Tool</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row, i) => (
                    <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-muted/20">
                      <td className="px-5 py-4 text-sm font-medium">{row.feature}</td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.buildxact} state={row.buildxactState} /></td>
                      <td className="px-5 py-4 text-sm"><Cell value={row.tpt} state={row.tptState} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-4 max-w-2xl">
               Competitor pricing and feature information was checked on 19 September 2026. Prices and features may have changed. Verify directly with Buildxact before making a purchasing decision.
            </p>
          </div>
        </section>

        {/* Pricing comparison */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Pricing comparison</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Buildxact UK list pricing is £119 Foundation, £209 Pro and £349 Master monthly, or annual-equivalent £89, £160 and £262. Feature pages advertise a 14-day trial. Payment-detail requirements should be confirmed with Buildxact.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border p-6 bg-muted/20">
                <h3 className="font-bold text-lg mb-3">Buildxact</h3>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />£119 Foundation, £209 Pro, £349 Master monthly</li>
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Annual-equivalent prices £89, £160 and £262</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Credit card required to trial</li>
                  <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 flex-shrink-0 mt-0.5" />Significant setup time before first quote</li>
                  <li className="flex items-start gap-2"><Minus className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />Supplier catalogues/live pricing, not a universal UK cost book</li>
                </ul>
              </div>
              <div className="rounded-xl border border-primary bg-primary/5 p-6">
                <h3 className="font-bold text-lg mb-3 text-primary">The Proper Tool</h3>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />From £29/month (per account)</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />14-day free trial</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No credit card required</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />UK cost library built in. First quote in under 10 minutes.</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />No per-seat fees: one price for the whole account</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Who should stay on Buildxact */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Who should stay on Buildxact</h2>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Buildxact is genuinely strong for a specific use case. If the following describes your business, it may well be the right tool:
            </p>
            <div className="space-y-3">
              {STAY_ON_BUILDXACT.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why builders switch */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Reasons UK builders switch from Buildxact to The Proper Tool</h2>
            <p className="text-lg text-muted-foreground mb-8">The switch is typically driven by three things: UK pricing, quoting speed, and the type of work.</p>
            <div className="space-y-4">
              {SWITCH_REASONS.map((reason, i) => (
                <div key={i} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <p className="text-muted-foreground leading-relaxed">{reason}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 bg-primary/5 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Try The Proper Tool free for 14 days</h2>
            <p className="text-lg text-muted-foreground mb-8">
              UK-built quoting software for residential builders. Generate a professional, fully itemised construction quote from drawings or a description in under 10 minutes.
            </p>
            <Button size="lg" asChild>
              <Link href="/login">
                Start free, no credit card needed
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · Plans from £29/mo · Cancel any time</p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Disclaimer */}
        <section className="py-8 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <p className="text-xs text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Disclaimer:</strong> Competitor pricing and feature information on this page is based on publicly available data and is correct to the best of our knowledge at the time of publication. Prices and features may have changed. We recommend verifying current details directly with Buildxact before making a purchasing decision.
            </p>
          </div>
        </section>

        {/* Related guides */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Also comparing</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                { href: "/best/quoting-software-for-uk-builders", label: "Full comparison: all 6 quoting tools for UK builders" },
                { href: "/estimatorxpress-alternative", label: "EstimatorXpress Alternative" },
                { href: "/simpro-alternative", label: "simPRO Alternative for Small Builders" },
                { href: "/tradify-alternative", label: "Tradify Alternative" },
                { href: "/comparisons", label: "All Software Comparisons" },
                { href: "/quoting-software-for-builders", label: "Quoting Software for Builders" },
              ].map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/40 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} The Proper Tool. Built for UK builders.</p>
          <div className="flex gap-6">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
            <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
